import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Breadcrumbs, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import AppShell from '../components/AppShell';
import api from '../services/api';
import { wranglerError } from './errors';
import QuotasTab from './QuotasTab';
import HiddenQuestions from './HiddenQuestions';
import RulesTab from './RulesTab';
import WranglerSection from './WranglerSection';

const TABS = [
  { id: 'quotas', label: 'Quotas' },
  { id: 'rules', label: 'Rules' },
  { id: 'setup', label: 'Setup' },
];

/**
 * A survey's Wrangler page (design §1): Quotas (the default), Rules and Setup tabs. A survey not
 * registered in Wrangler sees only the set-up offer.
 */
const WranglerPage = () => {
  const { surveyId } = useParams();
  const [params, setParams] = useSearchParams();
  const [survey, setSurvey] = useState(null);
  const [status, setStatus] = useState({ loading: true, registered: null, error: null });
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'quotas';

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => setSurvey(res.data));
    api.get(`/surveys/${surveyId}/wrangler/`)
      .then((res) => setStatus({ loading: false, registered: res.data.registered, error: null }))
      .catch((err) => setStatus({ loading: false, registered: null, error: wranglerError(err) }));
  }, [surveyId]);

  // Registering from the Setup section switches the page to its tabs.
  const onStatus = (data) => setStatus((s) => ({ ...s, registered: data.registered, error: null }));

  if (!survey) return null;

  // Not registered, or Wrangler's status unknown: the Setup section offers to set it up, or explains.
  const tabsShown = status.registered === true;

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Typography color="textPrimary">Wrangler</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <HubRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Wrangler
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Stack&apos;s quota service. It runs alongside this survey&apos;s Alchemer quotas
        (Alchemer ID {survey.alchemer_survey_id}).
      </Typography>

      {status.loading && <CircularProgress size={24} />}
      {status.error && status.registered === null && <Alert severity="error" sx={{ mb: 2 }}>{status.error}</Alert>}

      {!status.loading && !tabsShown && <WranglerSection survey={survey} onStatus={onStatus} />}

      {tabsShown && (
        <>
          <Tabs value={tab} onChange={(e, v) => setParams(v === 'quotas' ? {} : { tab: v }, { replace: true })}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            {TABS.map((t) => <Tab key={t.id} value={t.id} label={t.label} sx={{ textTransform: 'none', fontSize: 15 }} />)}
          </Tabs>
          {tab === 'quotas' && <QuotasTab survey={survey} />}
          {tab === 'rules' && <RulesTab survey={survey} />}
          {tab === 'setup' && (
            <>
              <WranglerSection survey={survey} onStatus={onStatus} />
              <HiddenQuestions survey={survey} />
            </>
          )}
        </>
      )}
    </AppShell>
  );
};

export default WranglerPage;
